import { S01_DOCUMENT_SLOTS, type S01DocumentCategory } from './s01Documents.js';

export const S01_DOMAINS = [
    'Workplace Safety',
    'Environmental Threats',
    'Physical Security',
    'Guard Force',
    'Parking Security',
    'Internal Controls',
    'Employee Controls',
    'Emergency Procedures',
    'Asset Protection',
] as const;

export type S01Domain = typeof S01_DOMAINS[number];
export type S01Answer = 'unanswered' | 'yes' | 'no' | 'na';
export type S01RiskLevel = 'Unrated' | 'Low' | 'Medium' | 'High' | 'Critical';

export type S01Intake = {
    facilityName: string;
    facilityAddress: string;
    facilityType: string;
    industrySector: string;
    floorsSquareFootage: string;
    headcount: string;
    operatingHours: string;
    tenantType: string;
    assessmentObjectives: string;
};

export type S01ChecklistDefinition = {
    id: string;
    key: string;
    domain: S01Domain;
    question: string;
    riskWhen: 'yes' | 'no';
    weight: number;
};

export type S01ChecklistItem = S01ChecklistDefinition & {
    answer: S01Answer;
    needsConsultantVerification: boolean;
    confidence: 'high' | 'medium' | 'low' | 'unverified';
    evidence: string;
    sourceUrls: string[];
    consultantComment: string;
};

export type S01ClientAnswer = { id: string; answer: S01Answer; needsConsultantVerification: boolean; evidence: string };
export type S01ReturnedQuestion = { id: string; note: string };

export type S01SourceRecord = {
    title: string;
    url: string;
    publisher: string;
    accessedAt: string;
    supports: string[];
};

export type S01DomainFinding = {
    domain: S01Domain;
    summary: string;
    strengths: string[];
    gaps: string[];
    recommendations: string[];
};

export type S01ActionItem = {
    id: string;
    action: string;
    priority: 'Immediate' | 'High' | 'Medium' | 'Low';
    owner: string;
    targetDate: string;
    status: 'Open' | 'Planned' | 'Complete';
    relatedChecklistIds: string[];
};

export type S01Document = {
    name: string;
    path: string;
    contentType: string;
    size: number;
    category?: S01DocumentCategory;
    status?: 'pending' | 'read' | 'unreadable';
    note?: string;
};

export type S01Gap = {
    id: string;
    domain?: S01Domain;
    severity: 'Critical' | 'High' | 'Medium' | 'Low';
    description: string;
    recommendation: string;
    relatedChecklistIds: string[];
};

export type S01Assessment = {
    workflowVersion: 'S01-v2' | 'S01-v3';
    noDocuments?: boolean;
    missingDocuments?: S01DocumentCategory[];
    documents?: S01Document[];
    gaps?: S01Gap[];
    generatedInputFingerprint?: string;
    reference: string;
    intake: S01Intake;
    executiveSummary: string;
    siteContext: string;
    mapUrl: string;
    crimeProfile: string;
    incidentHistory: string;
    asisBenchmark: string;
    researchGaps: string[];
    sources: S01SourceRecord[];
    checklist: S01ChecklistItem[];
    clientChecklist: S01ClientAnswer[];
    clientChecklistStatus: 'draft' | 'submitted' | 'changes_requested';
    clientChecklistVersion: number;
    clientChecklistSubmittedAt?: string | null;
    returnedQuestions: S01ReturnedQuestion[];
    answerRevision: number;
    generatedForRevision: number;
    domainFindings: S01DomainFinding[];
    actionPlan: S01ActionItem[];
    riskScore: number;
    riskLevel: S01RiskLevel;
    coveragePercent: number;
    unresolvedCount: number;
    domainScores: Record<S01Domain, number | null>;
    qa: { checked: number[]; signature: string };
    generatedAt?: string | null;
    generationStartedAt?: string | null;
    updatedAt?: string | null;
    publishedAt?: string | null;
    signedAt?: string | null;
    publishStartedAt?: string | null;
    draftStatus?: 'generating' | 'ready' | 'edited' | 'publishing' | 'published' | 'error' | '';
    draftError?: string;
};

export const S01_QA_ITEMS = [
    'All checklist answers have supporting evidence or consultant confirmation.',
    'Facility identity, address, size, occupancy, and operating profile were verified.',
    'External crime, incident, and site-context sources were reviewed for relevance.',
    'Risk scores and domain findings match the final checklist answers.',
    'Action-plan priorities, owners, and target dates are appropriate.',
    'ASIS references are advisory and make no certification claim.',
] as const;

const domainQuestions: Record<S01Domain, Array<string | [string, 'yes' | 'no']>> = {
    'Workplace Safety': [
        'Is a current workplace violence prevention policy approved and communicated?',
        'Can personnel report concerning behavior through confidential channels?',
        'Do personnel receive recurring workplace violence awareness training?',
        'Is a multidisciplinary threat assessment team established?',
        'Are high-risk terminations planned with security involvement?',
        'Are domestic violence concerns addressed through workplace procedures?',
        'Is an insider-threat identification and response process maintained?',
        'Are supervisors trained to recognize escalating or aggressive behavior?',
        'Are workplace violence reports documented and retained?',
        'Are employee assistance and support resources readily available?',
        'Do human resources, legal, and security coordinate case management?',
        'Is the weapons policy lawful, clear, and consistently enforced?',
        'Are lone-worker risks assessed and controlled?',
        'Does policy prohibit retaliation for good-faith security reporting?',
        'Are lessons from incidents incorporated into corrective actions?',
        'Is the workplace safety program reviewed at least annually?',
    ],
    'Environmental Threats': [
        ['Are violent-crime conditions materially elevated near the facility?', 'yes'],
        ['Are burglary or property-crime conditions materially elevated nearby?', 'yes'],
        ['Are robbery or carjacking conditions materially elevated nearby?', 'yes'],
        ['Are vehicle theft or vandalism conditions materially elevated nearby?', 'yes'],
        ['Have significant protests occurred near the facility?', 'yes'],
        ['Have riots or civil disturbances affected the operating area?', 'yes'],
        ['Do credible extremist or terrorism threats affect the operating area?', 'yes'],
        ['Do organized-crime activities affect the operating area?', 'yes'],
        ['Do kidnapping or extortion threats affect personnel locally?', 'yes'],
        ['Do nearby sensitive sites increase the facility threat profile?', 'yes'],
        ['Has fire disrupted facility operations within ten years?', 'yes'],
        ['Has hazardous-material release disrupted operations within ten years?', 'yes'],
        ['Has severe weather disrupted operations within ten years?', 'yes'],
        ['Has flooding disrupted operations within ten years?', 'yes'],
        ['Has earthquake activity disrupted operations within ten years?', 'yes'],
        ['Has civil unrest disrupted operations within ten years?', 'yes'],
        ['Has transportation disruption affected site access within ten years?', 'yes'],
        ['Has electrical utility loss disrupted operations within ten years?', 'yes'],
        ['Has water loss disrupted operations within ten years?', 'yes'],
        ['Has telecommunications loss disrupted operations within ten years?', 'yes'],
        ['Has network loss disrupted operations within ten years?', 'yes'],
        ['Has access-control failure disrupted operations within ten years?', 'yes'],
        ['Has HVAC failure disrupted operations within ten years?', 'yes'],
        ['Has elevator failure disrupted operations within ten years?', 'yes'],
        ['Has sewage failure disrupted operations within ten years?', 'yes'],
        ['Has intellectual-property loss disrupted operations?', 'yes'],
        ['Do neighboring land uses create hazardous-material exposure?', 'yes'],
        ['Do railways, highways, ports, or airports create material exposure?', 'yes'],
        ['Do terrain or street patterns restrict emergency access?', 'yes'],
        ['Are natural surveillance conditions around the property poor?', 'yes'],
        ['Are vacant buildings or unmanaged spaces immediately adjacent?', 'yes'],
        ['Are reliable public incident records unavailable for the area?', 'yes'],
        'Are external threat conditions reviewed on a defined schedule?',
        'Are credible threat advisories distributed to site leadership?',
        'Are business interruption dependencies documented?',
        'Are alternate utility arrangements documented and tested?',
        'Are off-site assembly and continuity locations identified?',
        'Are critical suppliers assessed for local disruption exposure?',
        'Are environmental risk assumptions supported by dated sources?',
        'Are external threat changes linked to protective measures?',
    ],
    'Physical Security': [
        'Is the site boundary clearly defined and routinely inspected?',
        'Are fences, walls, gates, or natural barriers fit for assessed threats?',
        'Are pedestrian entrances limited to necessary controlled points?',
        'Are vehicle entrances limited to necessary controlled points?',
        'Are doors and frames resistant to expected forced-entry methods?',
        'Are exterior doors self-closing and positively latching?',
        'Are windows protected according to their exposure and accessibility?',
        'Are roof, basement, loading, and service access points controlled?',
        'Is exterior lighting sufficient and maintained?',
        'Is interior security lighting sufficient and maintained?',
        'Is electronic access control used where risk warrants it?',
        'Are access permissions role-based and promptly updated?',
        'Are mechanical keys controlled, inventoried, and recoverable?',
        'Are master keys and override credentials tightly restricted?',
        'Does intrusion detection cover critical perimeter openings?',
        'Are intrusion alarms tested and responded to through written procedures?',
        'Does video surveillance cover critical approaches and access points?',
        'Are camera views, lighting, retention, and time synchronization adequate?',
        'Is surveillance footage access logged and restricted?',
        'Are reception and lobby areas arranged for visitor control?',
        'Are loading docks and receiving areas separated from occupied areas?',
        'Are mail and package screening controls risk-appropriate?',
        'Are emergency exits protected without obstructing safe egress?',
        'Are stairwells, elevators, and utility spaces appropriately controlled?',
        'Are server, communications, and security rooms access-restricted?',
        'Are security systems supported by backup power?',
        'Are alarm communications redundant where necessary?',
        'Are intercoms, duress devices, and emergency phones available where needed?',
        'Are security-system failures monitored and escalated?',
        'Are physical-security maintenance records current?',
        'Are construction and renovation security risks managed?',
        'Are landlord and tenant security responsibilities documented?',
        'Are physical controls tested through drills or penetration exercises?',
        'Is the physical security design reviewed after material changes?',
    ],
    'Guard Force': [
        'Is the guard-force model documented and appropriate for assessed threats?',
        'Are guard staffing levels based on posts, duties, and relief needs?',
        'Are guards licensed, screened, and trained as locally required?',
        'Are post orders current, specific, and acknowledged?',
        'Are shift handovers documented and supervised?',
        'Are patrol routes, frequencies, and exceptions recorded?',
        'Can guards communicate reliably with supervisors and responders?',
        'Are guard response and escalation protocols tested?',
        'Are use-of-force rules lawful, clear, and trained?',
        'Is guard equipment appropriate, inspected, and controlled?',
        'Are contractor performance and incident records reviewed?',
        'Are guard quality and effectiveness independently audited?',
    ],
    'Parking Security': [
        'Are parking areas illuminated for safe observation?',
        'Are parking entrances, exits, and pedestrian paths monitored?',
        'Are employee, visitor, and restricted parking zones defined?',
        'Are unauthorized or suspicious vehicles identified and addressed?',
        'Are parking emergency communications and response procedures available?',
        'Are parking controls accessible while maintaining security?',
    ],
    'Internal Controls': [
        'Are security roles, authorities, and accountability documented?',
        'Are security policies centrally approved and version-controlled?',
        'Are policy exceptions documented, approved, and reviewed?',
        'Are security incidents reported through a defined process?',
        'Are incidents categorized, investigated, and closed?',
        'Are corrective actions assigned, tracked, and verified?',
        'Are security records retained according to policy and law?',
        'Are sensitive records protected from unauthorized access?',
        'Are visitors identity-checked before access?',
        'Are visitors sponsored, logged, badged, and escorted appropriately?',
        'Are visitor badges visibly distinct and recovered?',
        'Are contractors screened and controlled proportionate to access?',
        'Are vendors limited to authorized areas and times?',
        'Are deliveries verified before acceptance?',
        'Is screening scope documented and appropriate for assessed threats?',
        'Are prohibited items defined and consistently handled?',
        'Are access requests approved by accountable managers?',
        'Are access rights reviewed periodically?',
        'Are terminated or transferred personnel promptly deprovisioned?',
        'Are shared access credentials prohibited or tightly controlled?',
        'Are access logs reviewed for anomalies?',
        'Are after-hours access approvals and records maintained?',
        'Are restricted areas clearly marked and access-controlled?',
        'Are clean-desk and secure-storage rules established where needed?',
        'Are confidential discussions protected from casual exposure?',
        'Are document destruction methods appropriate and verified?',
        'Are removable media and portable devices controlled?',
        'Are photography and recording rules defined?',
        'Are lost badges, keys, and devices promptly reported?',
        'Are security investigations coordinated with legal and privacy teams?',
        'Are local emergency services familiar with the facility?',
        'Are security suppliers contractually required to protect information?',
        'Are security procurement requirements risk-based?',
        'Are security changes tested before production use?',
        'Are control failures tracked through resolution?',
        'Are security metrics reported to responsible leadership?',
        'Are recurring trends analyzed and addressed?',
        'Are audits independent from control owners where practical?',
        'Are legal and regulatory requirements mapped to controls?',
        'Is the internal control program reviewed at least annually?',
    ],
    'Employee Controls': [
        'Are pre-employment checks lawful, approved, and role-appropriate?',
        'Are identity and work-eligibility checks completed before access?',
        'Are sensitive roles subject to enhanced screening where lawful?',
        'Are confidentiality and acceptable-use obligations acknowledged?',
        'Do new personnel complete security orientation?',
        'Do employees display identification where required?',
        'Are employees trained to challenge or report unauthorized persons?',
        'Is recurring security awareness training completed and recorded?',
        'Are role or access changes promptly communicated to security?',
        'Are personnel concerns handled through fair, confidential procedures?',
        'Are travel and lone-work risks addressed for exposed roles?',
        'Are remote-work physical security expectations documented?',
        'Are temporary workers controlled proportionate to their access?',
        'Are conflicts of interest and outside activities disclosed where required?',
        'Are departures coordinated across management, security, HR, and IT?',
        'Are badges, keys, records, devices, and credentials recovered at departure?',
        'Are continuing confidentiality obligations reinforced at departure?',
        'Are employee-control practices periodically reviewed for effectiveness?',
    ],
    'Emergency Procedures': [
        'Is an all-hazards emergency plan approved and current?',
        'Are emergency roles and alternates assigned?',
        'Are alarm and mass-notification methods tested?',
        'Are evacuation routes, exits, and assembly areas documented?',
        'Are shelter-in-place locations and procedures documented?',
        'Are lockdown procedures appropriate and trained?',
        'Are fire detection, suppression, and response procedures maintained?',
        'Are medical response supplies and trained responders available?',
        'Are active assailant response options included in training?',
        'Are bomb threat and suspicious package procedures documented?',
        'Are hazardous-material emergency procedures appropriate?',
        'Are severe weather and natural-hazard procedures appropriate?',
        'Are emergency contacts current and readily available?',
        'Are people needing assistance included in emergency planning?',
        'Are visitors and contractors included in accountability procedures?',
        'Are emergency exercises conducted on a defined schedule?',
        'Are exercise and incident lessons tracked to closure?',
        'Are emergency plans coordinated with public responders?',
    ],
    'Asset Protection': [
        'Are critical assets identified, owned, and classified?',
        'Are asset protection levels based on value, sensitivity, and impact?',
        'Are high-value assets stored in appropriately protected areas?',
        'Are asset inventories accurate and periodically reconciled?',
        'Are asset movements authorized and recorded?',
        'Are receiving, storage, and shipping duties appropriately separated?',
        'Are sensitive documents and media inventoried and secured?',
        'Are prototypes, intellectual property, and trade secrets specially protected?',
        'Are cash, negotiable items, and valuable materials controlled?',
        'Are tools and materials usable for unauthorized entry controlled?',
        'Are losses, shortages, and unexplained variances investigated?',
        'Are asset disposal and destruction witnessed where required?',
        'Are third-party custodians subject to equivalent safeguards?',
        'Are critical assets covered by continuity and recovery plans?',
        'Are asset-protection controls tested against credible scenarios?',
        'Is the asset protection program reviewed after material changes?',
    ],
};

let checklistNumber = 0;
export const S01_CHECKLIST: S01ChecklistDefinition[] = S01_DOMAINS.flatMap(domain => domainQuestions[domain].map(entry => {
    checklistNumber += 1;
    const [question, riskWhen] = Array.isArray(entry) ? entry : [entry, 'no' as const];
    return {
        id: `S01-${String(checklistNumber).padStart(3, '0')}`,
        key: `s01_${String(checklistNumber).padStart(3, '0')}`,
        domain,
        question,
        riskWhen,
        weight: 1,
    };
}));

export function cleanS01Intake(raw: any): S01Intake {
    const value = (key: keyof S01Intake, maximum = 500) => String(raw?.[key] ?? '').trim().slice(0, maximum);
    return {
        facilityName: value('facilityName', 240),
        facilityAddress: value('facilityAddress', 500),
        facilityType: value('facilityType', 160),
        industrySector: value('industrySector', 160),
        floorsSquareFootage: value('floorsSquareFootage', 160),
        headcount: value('headcount', 100),
        operatingHours: value('operatingHours', 240),
        tenantType: value('tenantType', 100),
        assessmentObjectives: value('assessmentObjectives', 3000),
    };
}

export function missingS01Intake(intake: S01Intake): string[] {
    const labels: Record<keyof S01Intake, string> = {
        facilityName: 'Facility name', facilityAddress: 'Facility address', facilityType: 'Facility type',
        industrySector: 'Industry or sector', floorsSquareFootage: 'Floors and square footage',
        headcount: 'Headcount', operatingHours: 'Operating hours', tenantType: 'Tenant type',
        assessmentObjectives: 'Assessment objectives',
    };
    return (Object.keys(labels) as Array<keyof S01Intake>).filter(key => !intake[key]).map(key => labels[key]);
}

export function seedS01Checklist(): S01ChecklistItem[] {
    return S01_CHECKLIST.map(item => ({ ...item, answer: 'unanswered', needsConsultantVerification: false, confidence: 'unverified',
        evidence: '', sourceUrls: [], consultantComment: '' }));
}

export function seedS01ClientChecklist(): S01ClientAnswer[] {
    return S01_CHECKLIST.map(item => ({ id: item.id, answer: 'unanswered', needsConsultantVerification: false, evidence: '' }));
}

export function cleanS01Response(source: { answer?: unknown; needsConsultantVerification?: unknown }): Pick<S01ClientAnswer, 'answer' | 'needsConsultantVerification'> {
    return { answer: source.answer === 'yes' || source.answer === 'no' || source.answer === 'na' ? source.answer : 'unanswered',
        needsConsultantVerification: source.needsConsultantVerification === true || source.answer === 'needs_verification' };
}

export function s01ClientAnsweredCount(checklist: S01ClientAnswer[]): number {
    let count = 0;
    for (let index = 0; index < checklist.length; index++)
        if (checklist[index].answer === 'yes' || checklist[index].answer === 'no' || checklist[index].answer === 'na')
            count += 1;
    return count;
}

export function calculateS01Risk(checklist: S01ChecklistItem[]) {
    const domainScores = Object.fromEntries(S01_DOMAINS.map(domain => [domain, null])) as Record<S01Domain, number | null>;
    let answered = 0;
    let unresolved = 0;
    let scored = 0;
    let risk = 0;
    for (let index = 0; index < checklist.length; index++) {
        const item = checklist[index];
        const response = cleanS01Response(item);
        if (response.needsConsultantVerification || response.answer === 'unanswered')
            unresolved += 1;
        if (item.answer === 'yes' || item.answer === 'no')
            answered += 1;
        if (item.answer !== 'yes' && item.answer !== 'no')
            continue;
        scored += item.weight;
        if (item.answer === item.riskWhen)
            risk += item.weight;
    }
    for (let domainIndex = 0; domainIndex < S01_DOMAINS.length; domainIndex++) {
        const domain = S01_DOMAINS[domainIndex];
        let domainScored = 0;
        let domainRisk = 0;
        for (let itemIndex = 0; itemIndex < checklist.length; itemIndex++) {
            const item = checklist[itemIndex];
            if (item.domain !== domain || item.answer !== 'yes' && item.answer !== 'no')
                continue;
            domainScored += item.weight;
            if (item.answer === item.riskWhen)
                domainRisk += item.weight;
        }
        domainScores[domain] = domainScored ? Math.round(domainRisk / domainScored * 100) : null;
    }
    const riskScore = scored ? Math.round(risk / scored * 100) : 0;
    const riskLevel: S01RiskLevel = !scored ? 'Unrated' : riskScore <= 15 ? 'Low' : riskScore <= 40 ? 'Medium' : riskScore <= 70 ? 'High' : 'Critical';
    return {
        riskScore,
        riskLevel,
        coveragePercent: checklist.length ? Math.round(answered / checklist.length * 100) : 0,
        unresolvedCount: unresolved,
        domainScores,
    };
}

export const s01DomainScoreLabel = (score: number | null | undefined) => score == null ? 'Unrated' : `${score}%`;

export function s01InformationGaps(checklist: S01ChecklistItem[], noDocuments: boolean, missingDocuments: S01DocumentCategory[] = []): S01Gap[] {
    const gaps: S01Gap[] = [];
    for (const slot of S01_DOCUMENT_SLOTS)
        if (missingDocuments.includes(slot.id))
            gaps.push({ id: `missing-document-${slot.id}`, domain: slot.domain, severity: 'Critical',
                description: `The client declared that the ${slot.title} is unavailable. Its documented procedures cannot be assessed.`,
                recommendation: `Provide or develop the ${slot.title} appropriate to the facility and verify its procedures.`, relatedChecklistIds: [] });
    if (noDocuments && !missingDocuments.length)
        gaps.push({ id: 'missing-policies', severity: 'Critical',
            description: 'The client declared that standard security documents are unavailable. Security policy and emergency planning information is missing.',
            recommendation: 'Establish and document facility security policies and emergency procedures appropriate to the facility.', relatedChecklistIds: [] });
    for (const domain of S01_DOMAINS) {
        const missing = checklist.filter(item => item.domain === domain && item.answer === 'unanswered');
        if (missing.length)
            gaps.push({ id: `information-${domain}`, domain, severity: 'Medium',
                description: `${domain}: supplied information does not establish ${missing.length} assessment criteria. These controls remain unknown.`,
                recommendation: `Obtain information about ${domain.toLowerCase()} and verify relevant facility controls.`,
                relatedChecklistIds: missing.map(item => item.id) });
    }
    return gaps;
}
